import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import Button from "../Button";
import { Input, Select, Option } from "../Form";
import Dialog, { DialogActions, DialogContent, DialogTitle } from "../Dialog";
import Spinner from "../Spinner";
import whatsappIcon from "../../assets/nopicture.png";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ApiErrorAlert from "../ApiErrorAlert";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import Grid from "../Grid";
import Paper from "../Paper";
import { Tab, Tabs } from "../Tabs";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import MessageCenterLink from "../MessageCenterLink";

const classes = {
  root: "flex flex-wrap",
  container: "flex flex-col gap-5",
  form: "flex min-h-0 flex-1 flex-col",
  mainPaper: "shrink-0 shadow-none rounded-none px-2",
  paper: "flex min-h-0 flex-1 flex-col shadow-none rounded-none",
  updateDiv: "flex w-full flex-col items-center justify-center gap-2 pb-2",
};

const backendUrl = getBackendUrl();

const formatDateForInput = (date) => {
  if (!date) return '';
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  return dateString;
};

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  password: Yup.string().min(5, "Muito curto!").max(50, "Muito longo!"),
  email: Yup.string().email("E-mail inválido").required("Obrigatório"),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId, selfEdit = false }) => {
  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled"
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  // const [allTicket, setAllTicket] = useState("disable");
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  // "Meu perfil" (selfEdit) mostra só dados pessoais, para qualquer perfil;
  // filas/conexão/permissões seguem no payload com os valores já carregados
  const canSeePermissions =
    !selfEdit && ["admin", "root"].includes(loggedInUser?.profile);
  const [avatar, setAvatar] = useState(null);
  const [error, setError] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate)
        }));

        const { profileImage } = data;
        if (profileImage) {
          setProfileUrl(
            `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
          );
        }

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        setError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setError(null);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    setError(null);
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return data.user.profileImage;
      } catch (error) {
        console.error("Erro no upload da imagem:", error);
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled"
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Recarregar página se for o usuário logado para atualizar a interface
      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {userId
            ? `${i18n.t("userModal.title.edit")}`
            : `${i18n.t("userModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form className={classes.form}>
              <Paper className={classes.mainPaper} elevation={1}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                  className={classes.tab}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  {canSeePermissions && (
                    <Tab
                      label={i18n.t("userModal.tabs.permissions")}
                      value={"permissions"}
                    />
                  )}
                </Tabs>
              </Paper>
              <ApiErrorAlert
                error={error}
                onClose={() => setError(null)}
                className="mx-6 mt-3 shrink-0"
              />
              <Paper className={classes.paper} elevation={0}>
                <DialogContent dividers>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"general"}
                  >
                    <div className={classes.updateDiv}>
                      <AvatarUploader
                        setAvatar={setAvatar}
                        avatar={user.profileImage}
                        companyId={user.companyId}
                      />
                      {user.profileImage && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            user.profileImage = null;
                            setFieldValue("profileImage", null);
                            setAvatar(null);
                          }}
                        >
                          {i18n.t("userModal.title.removeImage")}
                        </Button>
                      )}
                    </div>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={Input}
                          label={i18n.t("userModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={Input}
                          label={i18n.t("userModal.form.password")}
                          type="password"
                          name="password"
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={canSeePermissions ? 8 : 12}>
                        <Field
                          as={Input}
                          label={i18n.t("userModal.form.email")}
                          name="email"
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      {canSeePermissions && (
                        <Grid item xs={12} md={4}>
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <Field
                                as={Select}
                                fullWidth
                                label={i18n.t("userModal.form.profile")}
                                name="profile"
                                id="profile-selection"
                                required
                              >
                                <Option value="user">Usuário</Option>
                                <Option value="admin">Admin</Option>
                                {loggedInUser.profile === "root" && (
                                  <Option value="root">Root</Option>
                                )}
                              </Field>
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                    {canSeePermissions && (
                      <>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={12}>
                            <Can
                              role={loggedInUser.profile}
                              perform="user-modal:editQueues"
                              yes={() => (
                                <QueueSelect
                                  selectedQueueIds={selectedQueueIds}
                                  onChange={(values) => setSelectedQueueIds(values)}
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={12}>
                            <Can
                              role={loggedInUser.profile}
                              perform="user-modal:editProfile"
                              yes={() => (
                                <Field
                                  as={Select}
                                  fullWidth
                                  value={whatsappId}
                                  onChange={(e) => setWhatsappId(e.target.value)}
                                  label={i18n.t("userModal.form.whatsapp")}
                                >
                                  <Option value={""}>&nbsp;</Option>
                                  {whatsApps.map((whatsapp) => (
                                    <Option
                                      key={whatsapp.id}
                                      value={whatsapp.id}
                                    >
                                      {whatsapp.name}
                                    </Option>
                                  ))}
                                </Field>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6}>
                            <Field
                              as={Input}
                              label={i18n.t("userModal.form.startWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={startWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="startWork"
                              error={
                                touched.startWork && Boolean(errors.startWork)
                              }
                              helperText={touched.startWork && errors.startWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Field
                              as={Input}
                              label={i18n.t("userModal.form.endWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={endWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="endWork"
                              error={touched.endWork && Boolean(errors.endWork)}
                              helperText={touched.endWork && errors.endWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                        </Grid>
                      )}
                    />

                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={Input}
                          label="Data de Nascimento"
                          type="date"
                          name="birthDate"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          helperText="Data de nascimento para notificações de aniversário"
                          onChange={(e) => {
                            const formattedDate = parseDateFromInput(e.target.value);
                            setFieldValue('birthDate', formattedDate);
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Central de Mensagens é só para admin/root */}
                    {["admin", "root"].includes(loggedInUser?.profile) && (
                      <div className="my-2">
                        <MessageCenterLink section="users" id={userId} onNavigate={handleClose}>
                          Mensagem de despedida do atendente
                        </MessageCenterLink>
                      </div>
                    )}

                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Field
                              as={Select}
  fullWidth
                              label={i18n.t("userModal.form.defaultTheme")}
                              name="defaultTheme"
                              type="defaultTheme"
                              required
                            >
                              <Option value="light">
                                {i18n.t("userModal.form.defaultThemeLight")}
                              </Option>
                              <Option value="dark">
                                {i18n.t("userModal.form.defaultThemeDark")}
                              </Option>
                            </Field>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                              as={Select}
  fullWidth
                              label={i18n.t("userModal.form.defaultMenu")}
                              name="defaultMenu"
                              type="defaultMenu"
                              required
                            >
                              <Option value={"open"}>
                                {i18n.t("userModal.form.defaultMenuOpen")}
                              </Option>
                              <Option value={"closed"}>
                                {i18n.t("userModal.form.defaultMenuClosed")}
                              </Option>
                            </Field>
                      </Grid>
                    </Grid>
                  </TabPanel>
                  {canSeePermissions && (
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"permissions"}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.allTicket")}
                                    name="allTicket"
                                    type="allTicket"
                                    required
                                  >
                                    <Option value="enable">
                                      {i18n.t("userModal.form.allTicketEnable")}
                                    </Option>
                                    <Option value="disable">
                                      {i18n.t(
                                        "userModal.form.allTicketDisable"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.allowGroup")}
                                    name="allowGroup"
                                    type="allowGroup"
                                    required
                                  >
                                    <Option value={true}>
                                      {i18n.t("userModal.form.allTicketEnable")}
                                    </Option>
                                    <Option value={false}>
                                      {i18n.t(
                                        "userModal.form.allTicketDisable"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.allHistoric")}
                                    name="allHistoric"
                                    type="allHistoric"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.allUserChat")}
                                    name="allUserChat"
                                    type="allUserChat"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.userClosePendingTicket"
                                    )}
                                    name="userClosePendingTicket"
                                    type="userClosePendingTicket"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.allowSeeMessagesInPendingTickets")}
                                    name="allowSeeMessagesInPendingTickets"
                                    type="allowSeeMessagesInPendingTickets"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.allowConnections"
                                    )}
                                    name="allowConnections"
                                    type="allowConnections"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.showDashboard"
                                    )}
                                    name="showDashboard"
                                    type="showDashboard"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.allowRealTime"
                                    )}
                                    name="allowRealTime"
                                    type="allowRealTime"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.showContacts"
                                    )}
                                    name="showContacts"
                                    type="showContacts"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t(
                                      "userModal.form.showCampaign"
                                    )}
                                    name="showCampaign"
                                    type="showCampaign"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label={i18n.t("userModal.form.showFlow")}
                                    name="showFlow"
                                    type="showFlow"
                                    required
                                  >
                                    <Option value="disabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricDisabled"
                                      )}
                                    </Option>
                                    <Option value="enabled">
                                      {i18n.t(
                                        "userModal.form.allHistoricEnabled"
                                      )}
                                    </Option>
                                  </Field>
                            </Grid>
                          </Grid>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                    as={Select}
  fullWidth
                                    label="Finalização com Valor de Venda"
                                    name="finalizacaoComValorVendaAtiva"
                                    type="finalizacaoComValorVendaAtiva"
                                    required
                                  >
                                    <Option value="false">
                                      Desabilitado
                                    </Option>
                                    <Option value="true">Habilitado</Option>
                                  </Field>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    />
                  </TabPanel>
                  )}
                </DialogContent>
              </Paper>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  variant="secondary"
                  disabled={isSubmitting}
                >
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className="relative"
                >
                  {userId
                    ? `${i18n.t("userModal.buttons.okEdit")}`
                    : `${i18n.t("userModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <Spinner
                      size={24}
                      className="absolute left-1/2 top-1/2 -ml-3 -mt-3 text-green-500"
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;